import React, { FC, useCallback, useEffect, useState } from "react";
import { Container, Typography } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { RouteComponentProps, useLocation } from "react-router";
import { Helmet } from "react-helmet";
import { endOfTitle } from "../../../../constants/constants";
import { Form } from "react-final-form";
import Paper from "@material-ui/core/Paper";
import { NotifyMode } from "../../../notify/NotifyModeEnum";
import { mutateErrors } from "../../../../utils";
import { useRecaptcha } from "../../../../hooks/useRecaptcha";
import usePaymentQueries from "../../hooks/usePaymentQueries";
import { useNotify } from "../../../notify/useNotify";
import { ISubscriptionPayment } from "../../types";
import SubscriptionPaymentForm from "../../components/SubscriptionPaymentForm";
import { subscriptionPaymentValidator } from "../../validators/subscriptionPaymentValidator";
import * as queryString from "querystring";
import Loading from "../../../../components/Loading";
import Button from "../../../../components/Button";

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: 800,
  },
  cancelSubscriptionContainer: {
    height: 300,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  cancelationTitle: {
    fontSize: 32,
    lineHeight: "54px",
  },
  cancelationContent: {
    fontSize: 16,
  },
  footer: {
    marginTop: 16,
  },
  successText: {
    lineHeight: "180px",
    fontSize: 23,
    fontWeight: "bold",
  },
  registrationSuccessText: {
    lineHeight: "300px",
    fontSize: 23,
    fontWeight: "bold",
  },
}));

const SubscriptionPaymentPage: FC<RouteComponentProps> = () => {
  const classes = useStyles();
  const location = useLocation();
  const queryParams = queryString.parse(location.search.split("?").join(""));
  const [isCanceling, setIsCanceling] = useState<boolean>(false);
  const [isCanceledSubscription, setIsCanceled] = useState<boolean>(false);

  const { executeRecaptcha } = useRecaptcha();
  const { createSubscriptionCheckoutSession, cancelSubscription } =
    usePaymentQueries();
  const snackNotify = useNotify();

  const onCancelSubscription = useCallback(() => {
    if (typeof queryParams.cancel !== "string") {
      return;
    }

    setIsCanceling(true);

    cancelSubscription({ token: queryParams.cancel })
      .then(() => {
        setIsCanceled(true);
      })
      .catch((e) => {
        const { message } = mutateErrors(e);
        snackNotify("Error", message, NotifyMode.ERROR);
      })
      .finally(() => {
        setIsCanceling(false);
      });
  }, [cancelSubscription, queryParams.cancel, snackNotify]);

  const handleSubmit = useCallback(
    async (data) => {
      try {
        const recaptchaToken = await executeRecaptcha(
          "create_subscription_checkout_session",
        );

        const subscriptionPayment: ISubscriptionPayment = {
          name: data.name,
          email: data.email,
        };

        const {
          data: { redirectUrl },
        } = await createSubscriptionCheckoutSession(
          subscriptionPayment,
          recaptchaToken,
        );
        // redirecting to Stripe checkout page
        window.location.replace(redirectUrl);
      } catch (e) {
        const { message } = mutateErrors(e);
        snackNotify("Error", message, NotifyMode.ERROR);
      }
    },
    [createSubscriptionCheckoutSession, executeRecaptcha],
  );

  return (
    <Container maxWidth={"md"} className={classes.container}>
      <Helmet>
        <title>Subscribe{endOfTitle}</title>
      </Helmet>
      {isCanceling && <Loading />}
      <Paper>
        {queryParams.success && (
          <Container className={classes.cancelSubscriptionContainer}>
            <Typography
              component={"h1"}
              align="center"
              className={classes.registrationSuccessText}
            >
              Congratulations, you have successfully subscribed to our news!
            </Typography>
          </Container>
        )}
        {queryParams.cancel && (
          <Container className={classes.cancelSubscriptionContainer}>
            <Typography
              component={"h1"}
              align="center"
              className={classes.cancelationTitle}
            >
              We're sorry to see you go.
            </Typography>
            {!isCanceledSubscription && (
              <>
                <Typography
                  component={"h1"}
                  align="center"
                  className={classes.cancelationContent}
                >
                  By clicking on "Cancel Subscription," your subscription will
                  be terminated instantly, and you will no longer receive any
                  updates or notifications from our platform.
                </Typography>
                <Button
                  fullWidth
                  type="submit"
                  color="secondary"
                  variant="contained"
                  onClick={onCancelSubscription}
                >
                  Cancel Subscription
                </Button>
              </>
            )}
            {isCanceledSubscription && (
              <Typography
                component={"h1"}
                align="center"
                className={classes.successText}
              >
                Your subscription has been successfully canceled.
              </Typography>
            )}
          </Container>
        )}
        {!queryParams.success && !queryParams.cancel && (
          <>
            <Form
              onSubmit={handleSubmit}
              component={SubscriptionPaymentForm}
              validate={subscriptionPaymentValidator}
            />
            <Typography component="p" align="center" className={classes.footer}>
              This site is protected by reCAPTCHA and the Google{" "}
              <a href="https://policies.google.com/privacy">Privacy Policy</a>{" "}
              and{" "}
              <a href="https://policies.google.com/terms">Terms of Service</a>{" "}
              apply.
            </Typography>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default SubscriptionPaymentPage;
